/**
 * This is the entry point of an application.
 * See `@commercetools-frontend/application-shell` for usage.
 */
import { lazy, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Redirect, Route, Switch } from 'react-router-dom';
import * as globalActions from '@commercetools-frontend/actions-global';
import { PageNotFound } from '@commercetools-frontend/application-components';
import {
  ApplicationShell,
  setupGlobalErrorListener,
} from '@commercetools-frontend/application-shell';
import { Sdk } from '@commercetools-frontend/sdk';
import {
  ALL_DEFAULT_FLAGS,
  DEFAULT_SHORT_LIVED_FLAGS,
} from '../../constants/feature-toggles';
import applicationMessages from '../../messages';
import PageApplicationDisabled from '../page-application-disabled';

const AsyncWelcomeRoutes = lazy(() =>
  import('../../routes' /* webpackChunkName: "fallback" */)
);

const ReloadPage = () => {
  // The `forcedReload` ensures that the page is always reloaded
  // from the server, instead of from the browser cache.
  // https://developer.mozilla.org/en-US/docs/Web/API/Location/reload
  useEffect(() => {
    window.location.reload(/* forcedReload */ true);
  }, []);

  return null;
};
ReloadPage.displayName = 'ReloadPage';

// Ensure to setup the global error listener before any React component renders
// in order to catch possible errors on rendering/mounting.
setupGlobalErrorListener();

export const ApplicationFallback = (props) => (
  <>
    <Switch>
      <Redirect exact={true} from="/:projectKey" to="/:projectKey/welcome" />
      <Route path="/:projectKey/welcome">{props.children}</Route>
      {/**
       * NOTE:
       *  The user menu allows navigation to the application-account.
       *  When the application-fallback is rendered and the user intends
       *  to navigate to the application-account we need a fullpage reload
       *  as otherwise we remain within the application-fallback.
       */}
      <Route path="/account">
        <ReloadPage />
      </Route>
      <Route path="/:projectKey/disabled/:applicationName">
        <PageApplicationDisabled />
      </Route>
      <Route>
        <PageNotFound />
      </Route>
    </Switch>
  </>
);
ApplicationFallback.displayName = 'ApplicationFallback';
ApplicationFallback.propTypes = {
  children: PropTypes.node.isRequired,
};

const EntryPoint = () => (
  <ApplicationShell
    environment={window.app}
    featureFlags={DEFAULT_SHORT_LIVED_FLAGS}
    defaultFeatureFlags={ALL_DEFAULT_FLAGS}
    onRegisterErrorListeners={({ dispatch }) => {
      Sdk.Get.errorHandler = (error) =>
        globalActions.handleActionError(error, 'sdk')(dispatch);
    }}
    // This app works as a "catch-all" route and should only render
    // a 404 page.
    applicationMessages={applicationMessages}
    render={() => (
      <ApplicationFallback>
        <AsyncWelcomeRoutes />
      </ApplicationFallback>
    )}
  />
);

EntryPoint.displayName = 'EntryPoint';

export default EntryPoint;
